import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { ArrowRight, Clock, Tag } from 'lucide-react';
import { blogPosts } from '../mockData';

const SITE_URL = 'https://www.hexalogixautomation.com';

export const BlogPage = () => {
  const featured = blogPosts[0];
  const rest = blogPosts.slice(1);
  const pageUrl = `${SITE_URL}/blog`;
  const blogSchemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Hexalogix Automation Blog',
      description: 'Technical guides, industry trends, and expert knowledge on industrial automation, PLC programming, SCADA, BMS, and control panels.',
      url: pageUrl,
      publisher: {
        '@type': 'Organization',
        name: 'Hexalogix Automation',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.hexalogixautomation.com/Hexalogix.png'
        }
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: pageUrl }
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="Industrial Automation Blog | Hexalogix Automation UAE"
        description="Expert insights on PLC programming, control panel manufacturing, marine automation, SCADA, BMS and industry trends in UAE & Middle East."
        keywords="industrial automation blog UAE, PLC programming guide, control panel standards, SCADA HMI Middle East, marine automation, BMS UAE"
        canonical={pageUrl}
        schemas={blogSchemas}
      />

      {/* ═══ HERO ═══ */}
      <section className="relative bg-[#0A0F1E] pt-[106px] pb-16">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#0057B8]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
            <span className="text-[#0057B8] text-lg font-semibold tracking-widest uppercase">Knowledge Hub</span>
            <span className="w-12 h-[3px] bg-gradient-to-r from-transparent to-[#0057B8] rounded-full" />
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight mb-4">
            Automation Insights
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
            Technical guides, industry trends, and expert knowledge on industrial automation, control panels, SCADA, and more — written by our engineering team.
          </p>
        </div>
      </section>

      {/* ═══ FEATURED POST ═══ */}
      <section className="bg-[#F0F4F9] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to={`/blog/${featured.slug}`} className="group block">
            <div className={`relative bg-gradient-to-br ${featured.color} overflow-hidden border border-white/5 hover:border-[#0057B8]/40 transition-all duration-300 hover:shadow-2xl hover:shadow-[#0057B8]/10`}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#0057B8]/10 group-hover:bg-[#0057B8]/20 transition-colors duration-300"
                style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }} />
              <div className="relative p-8 md:p-12 max-w-3xl">
                <div className="flex items-center gap-3 mb-5">
                  <span className="bg-[#0057B8] text-white text-sm font-bold tracking-widest uppercase px-3 py-1">
                    Featured
                  </span>
                  <span className="flex items-center gap-1 text-gray-400 text-sm">
                    <Tag className="w-3 h-3" />{featured.category}
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-white leading-tight mb-4 group-hover:text-[#60A5FA] transition-colors">
                  {featured.title}
                </h2>
                <p className="text-gray-300 text-base leading-relaxed mb-6 max-w-2xl">
                  {featured.excerpt}
                </p>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-1 text-gray-400 text-base">
                    <Clock className="w-3.5 h-3.5" />{featured.readTime}
                  </div>
                  <span className="text-gray-500 text-base">{featured.date}</span>
                  <div className="flex items-center gap-1 text-[#0057B8] group-hover:text-[#60A5FA] text-base font-semibold transition-colors ml-auto">
                    Read Article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* ═══ POSTS GRID ═══ */}
      <section className="bg-[#F0F4F9] pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="group block">
                <div className={`h-full bg-gradient-to-br ${post.color} border border-white/5 hover:border-[#0057B8]/50 transition-all duration-300 hover:shadow-xl hover:shadow-[#0057B8]/10 flex flex-col overflow-hidden relative`}>
                  <div className="absolute top-0 right-0 w-24 h-24 bg-[#0057B8]/10 group-hover:bg-[#0057B8]/20 transition-colors duration-300"
                    style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }} />
                  <div className="p-6 flex flex-col flex-1 relative">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-[#60A5FA] text-sm font-semibold tracking-wide uppercase flex items-center gap-1">
                        <Tag className="w-3 h-3" />{post.category}
                      </span>
                    </div>
                    <h3 className="text-white font-bold text-lg leading-snug mb-3 group-hover:text-[#60A5FA] transition-colors flex-1">
                      {post.title}
                    </h3>
                    <p className="text-gray-400 text-base leading-relaxed mb-5 line-clamp-3 group-hover:text-gray-300 transition-colors">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between border-t border-white/10 pt-4 mt-auto">
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
                        <span>{post.date}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#0057B8] group-hover:translate-x-1 group-hover:text-[#60A5FA] transition-all" />
                    </div>
                  </div>
                  <div className="h-[2px] w-0 group-hover:w-full bg-[#0057B8] transition-all duration-500" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="bg-[#0057B8] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4">Have a Project in Mind?</h2>
          <p className="text-blue-100 mb-8">Our engineers are ready to discuss your automation requirements.</p>
          <Link to="/contact" className="w-full sm:w-auto">
            <button className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-white text-[#0057B8] font-bold px-5 sm:px-8 py-4 text-sm sm:text-base tracking-wide hover:bg-gray-100 transition-colors group">
              Get in Touch <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </Link>
        </div>
      </section>
    </>
  );
};
